/**
 * useCardTilt — subtle 3D cursor-following tilt on hover.
 *
 * Implements the specification from qam-animation-handoff.md & qam-animation-demos.html:
 * - Computes cursor relative position to card bounds (x, y in [-0.5, 0.5])
 * - Maximum tilt around 6–8° (rotateX / rotateY)
 * - Smooth reset on mouse leave
 * - Disabled on touch/mobile devices via (hover: hover) and (pointer: fine) check
 * - Respects prefers-reduced-motion: reduce
 */

export function useCardTilt(options: { maxTilt?: number; elevation?: number } = {}) {
  const maxTilt = options.maxTilt ?? 7; // 7° gives 6-8° range
  const elevation = options.elevation ?? -4; // subtle lift

  function isHoverSupported(): boolean {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function handleMouseMove(e: MouseEvent) {
    if (!isHoverSupported()) return;
    const el = e.currentTarget as HTMLElement;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;

    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;

    const tiltX = (y * -2 * maxTilt).toFixed(2);
    const tiltY = (x * 2 * maxTilt).toFixed(2);

    el.style.transform = `perspective(600px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(${elevation}px)`;
  }

  function handleMouseLeave(e: MouseEvent) {
    if (!isHoverSupported()) return;
    const el = e.currentTarget as HTMLElement;
    el.style.transform = '';
  }

  return {
    handleMouseMove,
    handleMouseLeave,
  };
}
